import React, { useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { hasActiveSubscription } from "../services/subscription";
import { toast } from "sonner";

const ProtectedRoute = ({ children, requireSubscription = true }) => {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(null);

  // Removed verbose logging for cleaner console

  // Set a shorter timeout to prevent infinite loading
  useEffect(() => {
    let timer;
    if (isLoading) {
      timer = setTimeout(() => {
        setLoadingTimeout(true);
      }, 1000);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isLoading]);

  // Listen for payment completion event to clear cache
  useEffect(() => {
    const handlePaymentComplete = () => {
      if (user?.id) {
        const cacheKey = `subscription_${user.id}`;
        sessionStorage.removeItem(cacheKey);
        sessionStorage.removeItem(`${cacheKey}_time`);
        setHasSubscription(null); // Force recheck
      }
    };

    window.addEventListener("payment-complete", handlePaymentComplete);
    return () =>
      window.removeEventListener("payment-complete", handlePaymentComplete);
  }, [user]);

  // Check subscription status when user is authenticated (with caching to prevent re-checks)
  useEffect(() => {
    const checkSubscription = async () => {
      if (user && requireSubscription) {
        // Check if we already have a cached result
        const cacheKey = `subscription_${user.id}`;
        const cached = sessionStorage.getItem(cacheKey);
        const cacheTime = sessionStorage.getItem(`${cacheKey}_time`);

        // Use cache if it's less than 1 minute old (reduced from 5 minutes for faster updates)
        if (cached && cacheTime && Date.now() - parseInt(cacheTime) < 60000) {
          setHasSubscription(JSON.parse(cached));
          return;
        }

        setIsCheckingSubscription(true);
        try {
          const isActive = await hasActiveSubscription(user.id);
          setHasSubscription(isActive);

          // Cache the result
          sessionStorage.setItem(cacheKey, JSON.stringify(isActive));
          sessionStorage.setItem(`${cacheKey}_time`, Date.now().toString());
        } catch (error) {
          console.error("Error checking subscription:", error);
          setHasSubscription(false);
        } finally {
          setIsCheckingSubscription(false);
        }
      }
    };

    if (isAuthenticated && requireSubscription && hasSubscription === null) {
      checkSubscription();
    }
  }, [user, requireSubscription, isAuthenticated, hasSubscription]);

  // If we're still loading authentication state or checking subscription and haven't hit the timeout, show a loading indicator
  if (
    (isLoading || (requireSubscription && isCheckingSubscription)) &&
    !loadingTimeout
  ) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          background: "var(--background)",
          color: "var(--foreground)",
          fontFamily: '"Plus Jakarta Sans", sans-serif',
        }}
      >
        <div
          style={{
            padding: "2rem",
            borderRadius: "8px",
            textAlign: "center",
            maxWidth: "400px",
            animation: "fadeIn 0.5s ease forwards",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              border: "3px solid var(--border)",
              borderTopColor: "var(--accent)",
              borderRadius: "50%",
              margin: "0 auto 1.5rem",
              animation: "spin 1s linear infinite",
            }}
          ></div>
          <h2
            style={{
              fontSize: "1.5rem",
              fontWeight: "500",
              marginBottom: "0.75rem",
              background:
                "linear-gradient(135deg, var(--foreground) 0%, var(--accent) 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            Loading
          </h2>
        </div>
        <style jsx="true">{`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes spin {
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    // Redirect to auth page, but save the attempted path
    return <Navigate to="/auth" state={{ from: location }} />;
  }

  // Check subscription if required
  if (requireSubscription && hasSubscription === false) {
    // Only show the toast if we're not already on the pricing page and haven't shown it recently
    if (location.pathname !== "/pricing") {
      const toastShownTime = sessionStorage.getItem("subscription_toast_shown");
      const currentTime = new Date().getTime();

      // Only show toast if it hasn't been shown in the last 5 seconds
      if (!toastShownTime || currentTime - parseInt(toastShownTime) > 5000) {
        toast.error("You need an active subscription to access this page");
        sessionStorage.setItem(
          "subscription_toast_shown",
          currentTime.toString(),
        );
      }
    }

    return <Navigate to="/pricing" state={{ from: location }} replace />;
  }

  return children;
};

export default ProtectedRoute;
